import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BookOpen, Loader2 } from "lucide-react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { fetchMinistrySampleTask } from "../lib/api";

/**
 * Coverage Tracker popover — fetches the Ontario Ministry-published
 * sample task for a (grade, expectationCode) pair on first open and
 * caches the result for subsequent opens of the same instance.
 *
 * Renders as a small icon button next to the expectation code; clicking
 * opens a 480px-wide scrollable card with the Ministry's expectation
 * text + sample task body in markdown.
 *
 * Falls back to a friendly empty-state when the Ministry doc has no
 * published task for the code (true for some F. Financial Literacy
 * codes and certain overall expectations).
 */
export default function MinistrySampleTaskPopover({ grade, code, label }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [task, setTask] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const handleOpenChange = async (next) => {
    setOpen(next);
    if (next && !loaded && !loading) {
      setLoading(true);
      setError("");
      try {
        const data = await fetchMinistrySampleTask(grade, code);
        setTask(data);
        setLoaded(true);
      } catch (e) {
        setError(
          e?.response?.data?.detail ||
            e?.message ||
            "Couldn't load the Ministry sample task right now.",
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const testId = `ministry-task-${grade.replace(/\s+/g, "-")}-${code}`;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testId}-trigger`}
          aria-label={`Show the Ministry sample task for ${label || code}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center h-5 w-5 rounded-full border border-[#4a6984]/25 bg-white/80 text-[#4a6984] hover:bg-[#96ded1]/35 hover:border-[#4a6984]/45 transition-colors shrink-0"
        >
          <BookOpen className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={8}
        className="w-[480px] max-w-[92vw] max-h-[70vh] overflow-y-auto p-0 border border-[#4a6984]/20 shadow-xl"
        data-testid={`${testId}-content`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-[#1c2d3d] text-white px-4 py-2.5 border-b border-[#1c2d3d]">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] opacity-70">
            Ontario Ministry sample task
          </p>
          <p className="text-base font-black tracking-tight">
            {grade} · {code}
          </p>
        </div>

        <div className="p-4 text-[13px] text-[#1c2d3d] leading-relaxed">
          {loading && (
            <div
              className="flex items-center gap-2 text-[#4a6984]"
              data-testid={`${testId}-loading`}
            >
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading the Ministry's sample task…</span>
            </div>
          )}
          {!loading && error && (
            <p
              className="text-[#9b3a3a] text-sm"
              data-testid={`${testId}-error`}
            >
              {error}
            </p>
          )}
          {!loading && !error && loaded && !task && (
            <p
              className="text-[#4a6984]/85"
              data-testid={`${testId}-empty`}
            >
              The Ontario Ministry doesn't publish a sample task for
              this specific expectation. Use the engine's generated
              task or your own.
            </p>
          )}
          {!loading && !error && task && (
            <>
              {task.expectation && (
                <div
                  className="mb-3 pb-3 border-b border-[#4a6984]/15"
                  data-testid={`${testId}-expectation`}
                >
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70 mb-1">
                    Expectation
                  </p>
                  <p className="text-[#1c2d3d] text-[13px] leading-snug">
                    {task.expectation}
                  </p>
                </div>
              )}
              <div
                className="ministry-task-body"
                data-testid={`${testId}-body`}
              >
                {task.sample_task_markdown ? (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({ node, ...props }) => (
                        <p className="mb-2.5 last:mb-0" {...props} />
                      ),
                      ul: ({ node, ...props }) => (
                        <ul
                          className="list-disc ml-5 mb-2.5 space-y-1"
                          {...props}
                        />
                      ),
                      ol: ({ node, ...props }) => (
                        <ol
                          className="list-decimal ml-5 mb-2.5 space-y-1"
                          {...props}
                        />
                      ),
                      li: ({ node, ...props }) => (
                        <li className="text-[13px]" {...props} />
                      ),
                      code: ({ node, inline, ...props }) =>
                        inline ? (
                          <code
                            className="bg-[#f4f5f7] px-1 py-0.5 rounded text-[12px]"
                            {...props}
                          />
                        ) : (
                          <code
                            className="block bg-[#f4f5f7] p-2 rounded text-[12px] my-2 overflow-x-auto"
                            {...props}
                          />
                        ),
                      strong: ({ node, ...props }) => (
                        <strong
                          className="font-bold text-[#1c2d3d]"
                          {...props}
                        />
                      ),
                    }}
                  >
                    {task.sample_task_markdown}
                  </ReactMarkdown>
                ) : (
                  <p className="text-[#4a6984]/85 italic">
                    The Ministry lists this expectation without a
                    published sample task — adapt your own.
                  </p>
                )}
              </div>
              <p className="mt-4 pt-3 border-t border-[#4a6984]/15 text-[10px] text-[#4a6984]/65 leading-snug">
                Source: The Ontario Curriculum, Grades 1–8: Mathematics
                (2020). Ministry sample tasks are anchors, not scripts —
                adapt for your class context.
              </p>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
